/**
 * BaseDomain Abstract Class
 *
 * Abstract base class that all domain services extend. Provides common
 * functionality including API client integration, token management,
 * retry logic, and request deduplication.
 *
 * Features:
 * - Integration with ApiClient, TokenManager, and RequestManager
 * - Automatic token synchronization with ApiClient
 * - Helper methods for retry and deduplication
 * - Abstract methods that domains must implement
 * - Generic error handling with domain-specific error types
 *
 * @module services/shared/BaseDomain
 */

import { ApiClient } from './ApiClient';
import { TokenManager } from './TokenManager';
import { RequestManager } from './RequestManager';
import { BaseApiError } from './BaseApiError';
import type { RetryOptions } from './RequestManager';

/**
 * Abstract BaseDomain class that all domain services extend
 *
 * @template TError - Domain-specific error type extending BaseApiError
 */
export abstract class BaseDomain<TError extends BaseApiError> {
  protected apiClient: ApiClient<TError>;
  protected tokenManager: TokenManager;
  protected requestManager: RequestManager;

  /**
   * Initialize BaseDomain with shared services
   *
   * @param apiClient - HTTP client for API requests
   * @param tokenManager - Token management service
   * @param requestManager - Request retry and deduplication service
   */
  constructor(
    apiClient: ApiClient<TError>,
    tokenManager: TokenManager,
    requestManager: RequestManager
  ) {
    this.apiClient = apiClient;
    this.tokenManager = tokenManager;
    this.requestManager = requestManager;

    // Set up automatic token synchronization with ApiClient
    this.setupTokenSynchronization();
  }

  /**
   * Execute operation with retry logic and exponential backoff
   *
   * @param operation - The async operation to execute
   * @param retryOptions - Optional retry configuration
   * @returns Promise that resolves with the operation result
   * @throws The last error if all retries are exhausted
   */
  protected async executeWithRetry<T>(
    operation: () => Promise<T>,
    retryOptions?: RetryOptions
  ): Promise<T> {
    return this.requestManager.executeWithRetry(operation, retryOptions);
  }

  /**
   * Execute operation with request deduplication
   *
   * Prevents duplicate concurrent requests by caching and reusing pending promises.
   * Multiple calls with the same key will return the same promise.
   *
   * @param key - Unique identifier for the request
   * @param operation - The async operation to execute
   * @returns Promise that resolves with the operation result
   */
  protected async executeWithDeduplication<T>(
    key: string,
    operation: () => Promise<T>
  ): Promise<T> {
    return this.requestManager.executeWithDeduplication(key, operation);
  }

  /**
   * Create domain-specific error with consistent formatting
   *
   * @param operation - The operation that failed
   * @param message - Error message
   * @param status - HTTP status code (optional)
   * @param fieldErrors - Field-specific validation errors (optional)
   * @param code - Error code for programmatic handling (optional)
   * @returns Domain-specific error instance
   */
  protected createError(
    operation: string,
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ): TError {
    const domainName = this.getDomainName();
    const formattedMessage = import.meta.env.PROD ? `${message}` : `${domainName} ${operation}: ${message}`;

    return this.createDomainError(
      operation,
      formattedMessage,
      status,
      fieldErrors,
      code
    );
  }

  /**
   * Set up automatic token synchronization between TokenManager and ApiClient
   *
   * @private
   */
  private setupTokenSynchronization(): void {
    // Set initial token if available
    const currentToken = this.tokenManager.getToken();
    if (currentToken) {
      this.apiClient.setAuthToken(currentToken);
    }

    // Listen for token changes and update ApiClient
    this.tokenManager.onTokenChange((token) => {
      if (token) {
        this.apiClient.setAuthToken(token);
      } else {
        this.apiClient.clearAuthToken();
      }
    });
  }

  /**
   * Get the domain name for error messages and logging
   *
   * @returns Domain name (e.g., "Auth", "User", "Referral")
   */
  protected abstract getDomainName(): string;

  /**
   * Create domain-specific error instance
   *
   * Each domain service must implement this to return their specific error type.
   *
   * @param operation - The operation that failed
   * @param message - Error message
   * @param status - HTTP status code (optional)
   * @param fieldErrors - Field-specific validation errors (optional)
   * @param code - Error code for programmatic handling (optional)
   * @returns Domain-specific error instance
   */
  protected abstract createDomainError(
    operation: string,
    message: string,
    status?: number,
    fieldErrors?: Record<string, string>,
    code?: string
  ): TError;
}